import { useCallback, useEffect, useRef, useState } from 'react';
import { formatCurrency, formatShortDateWithoutTimezone } from '@ibk/shared-utils';
import { useLazyGetSlipListQuery } from '../../services/receiptApi/receiptApi';
import { getStatusType } from '../../utils';
export function useChargesList(options = {}) {
    const { orderBy = 'createdAt', sort = 'desc', pageSize = 10 } = options;
    const [trigger, { data, isLoading, error }] = useLazyGetSlipListQuery();
    const [allRows, setAllRows] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const isInitialLoad = useRef(true);
    const loadInitialData = useCallback(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            trigger({
                orderBy,
                sort,
                page: 1,
                pageSize,
                limit: pageSize,
            });
        }
    }, [trigger, orderBy, sort, pageSize]);
    const loadMore = useCallback(() => {
        if (hasMore && !isFetchingMore && !isLoading) {
            setIsFetchingMore(true);
            trigger({
                orderBy,
                sort,
                page: currentPage + 1,
                pageSize,
                limit: pageSize,
            });
        }
    }, [hasMore, isFetchingMore, isLoading, trigger, orderBy, sort, currentPage, pageSize]);
    const refresh = useCallback(() => {
        setIsRefreshing(true);
        setCurrentPage(1);
        setTotalPages(1);
        setHasMore(true);
        setIsFetchingMore(false);
        isInitialLoad.current = true;
        trigger({
            orderBy,
            sort,
            page: 1,
            pageSize,
            limit: pageSize,
        });
    }, [trigger, orderBy, sort, pageSize]);
    useEffect(() => {
        if (data?.result) {
            const { rows, page, pages } = data.result;
            const pageNum = parseInt(page, 10);
            setCurrentPage(pageNum);
            setTotalPages(pages);
            setHasMore(pageNum < pages);
            setIsFetchingMore(false);
            setIsRefreshing(false);
            if (pageNum === 1) {
                setAllRows(rows);
            }
            else {
                setAllRows(prev => [...prev, ...rows]);
            }
        }
    }, [data]);
    useEffect(() => {
        if (error) {
            setIsFetchingMore(false);
            setIsRefreshing(false);
        }
    }, [error]);
    const formatRows = (rowsToFormat) => rowsToFormat.map(row => ({
        id: row.id,
        issuing: row.createAt ? formatShortDateWithoutTimezone(row.createAt) : '',
        payer: row.debitor?.name ?? '',
        registration: row.debitor?.document ?? '',
        dueDate: row.duedate ? formatShortDateWithoutTimezone(row.duedate) : '',
        ourNumber: row.nossoNumero ?? '',
        amount: row.amount ? formatCurrency(row.amount) : '',
        status: row.status,
        statusType: getStatusType(row.status),
    }));
    const rows = formatRows(allRows);
    const rawRows = allRows;
    return {
        rows,
        rawRows,
        isLoading,
        error,
        total: allRows.length,
        page: currentPage,
        pages: totalPages,
        hasMore,
        isFetchingMore,
        isRefreshing,
        loadMore,
        loadInitialData,
        refresh,
    };
}
